import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Contracts } from "../lib/api";
import { Download, Trash2, FilePlus2, FileText, Eye } from "lucide-react";
import { toast } from "sonner";
import { useConfirm } from "../components/ConfirmProvider";

const TYPE_LABELS = {
  employment: "Umowa o pracę",
  mandate: "Umowa zlecenie",
  vehicle: "Powierzenie auta",
  hours_statement: "Wykaz godzin",
  rachunek: "Rachunek do umowy zlecenia",
};

export default function ContractHistory() {
  const { confirm } = useConfirm();
  const [list, setList] = useState([]);
  const [filter, setFilter] = useState("all");
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const data = await Contracts.list();
      setList(data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const filtered = filter === "all" ? list : list.filter((c) => c.contract_type === filter);

  const remove = async (id) => {
    if (!(await confirm("Usunąć tę umowę?", { tone: "danger", confirmLabel: "Usuń" }))) return;
    try {
      await Contracts.remove(id);
      toast.success("Umowa usunięta");
      load();
    } catch (err) {
      toast.error("Błąd: " + (err.response?.data?.detail || err.message));
    }
  };

  const filters = [
    { id: "all", label: "Wszystkie" },
    { id: "employment", label: "O pracę" },
    { id: "mandate", label: "Zlecenie" },
    { id: "vehicle", label: "Powierzenie auta" },
  ];

  return (
    <div className="space-y-8" data-testid="history-page">
      <div className="flex items-end justify-between">
        <div>
          <div className="font-label mb-1">// Archiwum</div>
          <h1 className="font-heading font-black text-4xl uppercase tracking-tight">
            Historia umów
          </h1>
        </div>
        <Link
          to="/contracts/new"
          data-testid="history-new-contract"
          className="inline-flex items-center gap-2 bg-[#E52C34] text-white px-5 py-3 font-bold uppercase tracking-wider hover:bg-[#C5232A] transition-colors"
        >
          <FilePlus2 className="w-4 h-4" /> Nowa Umowa
        </Link>
      </div>

      <div className="flex flex-wrap gap-0 border border-zinc-900 w-fit">
        {filters.map((f) => (
          <button
            key={f.id}
            onClick={() => setFilter(f.id)}
            data-testid={`filter-${f.id}`}
            className={`px-4 py-2 text-sm font-bold uppercase tracking-wider border-r border-zinc-900 last:border-r-0 ${
              filter === f.id
                ? "bg-zinc-900 text-white"
                : "bg-white text-zinc-900 hover:bg-zinc-100"
            }`}
          >
            {f.label}
          </button>
        ))}
      </div>

      <div className="border border-zinc-900 overflow-x-auto">
        <table className="w-full" data-testid="contracts-table">
          <thead>
            <tr className="bg-zinc-900 text-white text-left">
              <Th>Numer</Th>
              <Th>Typ</Th>
              <Th>Pracownik</Th>
              <Th>Okres</Th>
              <Th>Stawka</Th>
              <Th>Wygenerowano</Th>
              <Th className="text-right">Akcje</Th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr>
                <td colSpan={7} className="py-10 text-center text-sm text-zinc-500">
                  Ładowanie...
                </td>
              </tr>
            )}
            {!loading && filtered.length === 0 && (
              <tr>
                <td colSpan={7} className="py-16 text-center text-zinc-500">
                  <FileText className="w-10 h-10 mx-auto opacity-30 mb-3" />
                  Brak wygenerowanych umów w tej kategorii.
                </td>
              </tr>
            )}
            {filtered.map((c) => (
              <tr
                key={c.id}
                className="border-t border-zinc-200 hover:bg-zinc-50"
                data-testid={`contract-row-${c.id}`}
              >
                <td className="py-3 px-4 font-mono text-sm font-semibold">
                  <Link
                    to={`/contracts/${c.id}`}
                    className="hover:text-[#E52C34] underline-offset-4 hover:underline"
                  >
                    {c.contract_number}
                  </Link>
                </td>
                <td className="py-3 px-4 text-sm">{TYPE_LABELS[c.contract_type]}</td>
                <td className="py-3 px-4 text-sm">
                  {c.employee_snapshot?.first_name} {c.employee_snapshot?.last_name}
                </td>
                <td className="py-3 px-4 text-xs font-mono text-zinc-600">
                  {c.start_date || "—"} → {c.end_date || "—"}
                </td>
                <td className="py-3 px-4 font-mono text-sm">
                  {c.hourly_rate != null ? `${c.hourly_rate.toFixed(2)} zł` : "—"}
                </td>
                <td className="py-3 px-4 text-xs text-zinc-500 font-mono">
                  {new Date(c.created_at).toLocaleString("pl-PL")}
                </td>
                <td className="py-3 px-4 text-right whitespace-nowrap">
                  <Link
                    to={`/contracts/${c.id}`}
                    data-testid={`view-contract-${c.id}`}
                    className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-bold uppercase tracking-wider border border-zinc-300 hover:border-zinc-900 mr-2"
                  >
                    <Eye className="w-3 h-3" /> Podgląd
                  </Link>
                  <button
                    onClick={async () => {
                      try { await Contracts.download(c.id, "pdf"); toast.success("Pobrano PDF"); }
                      catch { toast.error("Błąd pobierania PDF"); }
                    }}
                    data-testid={`download-pdf-${c.id}`}
                    className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-bold uppercase tracking-wider border border-zinc-300 hover:border-zinc-900 mr-2"
                  >
                    <Download className="w-3 h-3" /> PDF
                  </button>
                  <button
                    onClick={async () => {
                      try { await Contracts.download(c.id, "docx"); toast.success("Pobrano DOCX"); }
                      catch { toast.error("Błąd pobierania DOCX"); }
                    }}
                    data-testid={`download-docx-${c.id}`}
                    className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-bold uppercase tracking-wider border border-zinc-300 hover:border-zinc-900 mr-2"
                  >
                    <Download className="w-3 h-3" /> DOCX
                  </button>
                  <button
                    onClick={() => remove(c.id)}
                    data-testid={`btn-delete-contract-${c.id}`}
                    className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-bold uppercase tracking-wider border border-zinc-300 text-red-600 hover:border-red-600"
                  >
                    <Trash2 className="w-3 h-3" />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function Th({ children, className = "" }) {
  return (
    <th className={`py-3 px-4 text-xs uppercase tracking-[0.2em] font-bold ${className}`}>
      {children}
    </th>
  );
}
